import { AdvisorSelection } from "../../models/EnrollmentHistoryDTO";

/*
	Lo que dice GET payments/_promoOptions (PromocionAdelantoService::opciones) y lo que la pantalla arma con ello. Lo usan el panel
	«Aplicar Promoción» de Registrar Pago (Caja) y la Solicitud de Promoción de la asesora (docs/ESPEC_SOLICITUD_PROMO.md).
*/

/** Mes que el cambio de promoción reabre: NO viene en los renglones cobrables (`_owed`); va en el pago con apply = difference. */
export interface ReopenedMonth { key: string; title: string; difference: number; /** Precio normal al que vuelve el mes (si el servidor lo manda). */ listAmount?: number; }
/** Producto que regala una alternativa (valor = precio de lista × cantidad). */
export interface GiftProduct { articleId: number; name: string; quantity: number; value: number }
/**
 * Alternativa de regalo de la promo (docs/ESPEC_PROMO_REGALOS.md): solo vienen las que le tocan al curso de la matrícula.
 * Si hay 2 o más, Caja elige una antes de aplicar; `blocked` = motivo por el que esa no se puede (un producto inactivo, ya lo tiene…).
 */
export interface GiftOption { key: string; label: string; items: GiftProduct[]; value: number; blocked?: string | null }
export interface PromoOption {
	enrollmentId: number; studentId?: number; campusId?: number; courseName: string; scheduleText: string; promotionId: number; name: string; kind: string;
	needed: number;
	/** Recargos vencidos de los meses que la promo pide pagar: van en el mismo pago como sus propios renglones (`surchargeKeys`). `charge` = needed + surcharges. */
	surcharges?: number; surchargeKeys?: string[]; charge?: number;
	deadline: string | null; blocked: string | null;
	/** El bloqueo es porque la promo toca a otra persona (grupo, 2x1, 50 %) o quita una beca: la asesora no la pide, la cambia Caja. */
	blockedLinked?: boolean; fits: "exacto" | "faltan" | "sobra" | null; diff: number | null;
	/** Renglones (llaves de `_owed`) que van COMPLETOS en el pago, sin promotionId: kit regalo y descuento de «todas juntas» los pone el servidor. */
	payKeys: string[];
	gives?: { months?: string[]; kit?: { articleId: number; name: string; value: number }; products?: GiftProduct[]; course?: boolean; discount?: number } | null;
	/** Lo que regala SIN los productos (meses, descuento); el total del regalo = giftValue + el valor de la alternativa elegida. */
	giftValue?: number;
	/** Alternativas de regalo que le tocan a esta matrícula (vacío o ausente = la promo no regala productos o el servidor es viejo). */
	giftOptions?: GiftOption[];
	/** La alternativa ya elegida por el servidor (cuando solo le toca una y no está bloqueada). */
	giftOptionKey?: string | null;
	replaces: string | null; reopens: number; reopenedMonths?: ReopenedMonth[];
	/** Ya está todo pagado a tiempo por otro camino (needed = 0): se aplica sin cobrar, con «Aplicar Sin Cobrar». */
	paidAlready?: boolean;
	/** Acuerdo «Pendiente de pago adelantado» que ya existía (solicitud vieja, Actualizaciones): solo se cumple aquí y se reusa. */
	agreed?: boolean; agreementId?: number | null;
	/** 2x1 con el curso regalado ya conocido (acuerdo o borrador). */
	giftEnrollmentId?: number | null; giftStudentId?: number | null;
}
/**
 * Lo que trae el ticket o el acuerdo pendiente. `studentId` = quien PAGA: en un 2x1 el ticket puede ser de quien recibe el regalo y el
 * panel se abre en la persona que paga (Esteban 2026-10-30).
 */
export interface PromoDraft {
	/** Promo En Camino viva de la que sale este borrador (docs/ESPEC_PROMO_EN_CAMINO.md): el panel se precarga con ella. */
	enCaminoId?: number | null;
	enrollmentId: number; promotionId: number; advisors?: any[] | null; giftEnrollmentId?: number | null; giftStudentId?: number | null;
	studentId?: number | null; studentName?: string | null; identityCode?: string | null; agreementId?: number | null;
	giftStudentName?: string | null; giftCourseName?: string | null; giftScheduleText?: string | null;
	/** La alternativa de regalo que se eligió (si la promo regala productos). */
	giftOptionKey?: string | null;
}
/** Respuesta de POST payments/_promoApplyPaid. */
export interface PromoAppliedPaid { promoApplicationId: number; promoName: string; kind: string; giftValue: number; giftOwedArticleId?: number | null; agreementId?: number | null }
/** Estados de una promo En Camino (status_id 1..6 en este orden). */
export type EnCaminoStatus = "porCobrar" | "enCamino" | "aplicada" | "fallida" | "cancelada" | "sinPago";
/** Objeto `EnCamino` del contrato (docs/CONTRATO_PROMO_EN_CAMINO.md §1): el mismo en todas las respuestas. */
export interface EnCamino {
	id: number; status: EnCaminoStatus; statusName: string; campusId?: number; studentId: number; studentName?: string | null; identityCode?: string | null;
	enrollmentId: number; courseName?: string | null; scheduleText?: string | null; promotionId: number; promoName: string; kind?: string | null;
	giftEnrollmentId?: number | null; giftStudentName?: string | null; giftCourseName?: string | null;
	target: number; minToday: number; paid: number; paidDay1?: number; remaining: number;
	startDate?: string | null; dueDate?: string | null; daysLeft?: number | null; attempt?: number;
	origin?: string | null; skipHalf?: boolean;
	/** Renglones apartados (meses a regalar o curso regalado): Caja no los cobra. */
	frozenKeys?: string[];
	/** Renglones que la promo pide pagar, en orden. */
	payKeys?: string[];
	advisors?: { userId: number | string | null; name: string; sharePercentage?: number | null }[];
	requestedBy?: { id: number; name: string } | null; createdBy?: { id: number; name: string } | null;
	endedAt?: string | null; endedReason?: string | null; applicationId?: number | null; approvalId?: number | null;
	currencySymbol?: string;
}
/** Detalle de una En Camino (GET promotions/_enCamino/{id}). */
export interface EnCaminoDetail extends EnCamino {
	history?: { at: string | null; text: string; by?: string | null }[];
	payments?: { receiptId: number; date: string | null; amount: number; method?: string | null }[];
}
/** Respuesta de POST payments: lo que pasó con la En Camino de ese pago. */
export interface EnCaminoPagoRes { id: number; status: EnCaminoStatus; paid: number; target: number; remaining: number; applied: boolean; text: string }

/** La promo escogida en el pago (una por pago). */
export interface PromoApp {
	/** Si la promo escogida es una En Camino viva: el dinero entra como abono en orden y se completa cuando llega a la meta. */
	enCamino?: EnCamino | null;
	studentId: number; option: PromoOption; advisors: AdvisorSelection[] | null;
	giftStudentId: number | null; giftEnrollmentId: number | null; giftLabel: string;
	/** 2x1: lo que dice la validación del servidor (GET _twoForOne) sobre el curso regalado; null = sin revisar todavía. */
	giftIssues: string[] | null;
	/** La alternativa de regalo elegida (`giftOptions[].key`); null = ninguna todavía o la promo no regala productos. */
	giftOptionKey: string | null;
}

export const KIND_DOS_X_UNO = "dos_x_uno";

export const giftOptionsOf = (o: PromoOption): GiftOption[] => (Array.isArray(o.giftOptions) ? o.giftOptions : []);
/**
 * Qué alternativa queda elegida al escoger (o refrescar) una promo: la que ya se había elegido o traía el borrador si sigue siendo válida;
 * si no, la que preseleccionó el servidor; si no, la única que hay (no bloqueada). Con 2 o más sin elegir → null (Caja elige).
 */
export const initialGiftKey = (o: PromoOption, wanted?: string | null): string | null => {
	const gs = giftOptionsOf(o);
	if (!gs.length) return null;
	if (wanted && gs.some((g) => g.key === wanted && !g.blocked)) return wanted;
	if (o.giftOptionKey && gs.some((g) => g.key === o.giftOptionKey && !g.blocked)) return o.giftOptionKey;
	return gs.length === 1 && !gs[0].blocked ? gs[0].key : null;
};

/** Lo que se cobra por la promo: lo que pide más los recargos de esos meses (el servidor compara el monto contra esto). */
export const chargeOf = (o: PromoOption): number => (o.charge !== undefined && o.charge !== null ? Number(o.charge) : Number(o.needed)) || 0;

export const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;

/** Cuadra / faltan / sobra contra lo que se escribió en el pago (el servidor manda lo mismo; esto se repinta al teclear). */
export const fitOf = (needed: number, received: number): { fits: "exacto" | "faltan" | "sobra" | null; diff: number } => {
	if (received < 0.005) return { fits: null, diff: 0 };
	if (Math.abs(received - needed) < 0.005) return { fits: "exacto", diff: 0 };
	return received < needed ? { fits: "faltan", diff: round2(needed - received) } : { fits: "sobra", diff: round2(received - needed) };
};

/** Qué falta en las asesoras (null = bien): todas con % que sumen 100, o todas vacías (el servidor reparte parejo). */
export const advisorsPending = (advisors: AdvisorSelection[] | null): string | null => {
	if (advisors === null) return "Elige las Asesoras";
	const pcts = advisors.map((a) => a.sharePercentage);
	if (pcts.some((x) => x !== null && x !== undefined)) {
		if (pcts.some((x) => x === null || x === undefined)) return "Pon el % de Cada Asesora";
		if (Math.abs(pcts.reduce((s: number, x) => s + Number(x), 0) - 100) > 0.009) return "Los % Deben Sumar 100";
	}
	return null;
};

/** Qué falta para poder registrar con esta promo (null = listo). */
export const promoPending = (app: PromoApp): string | null => {
	if (app.option.blocked) return app.option.blocked;
	const gs = giftOptionsOf(app.option);
	if (gs.length) {
		const g = gs.find((x) => x.key === app.giftOptionKey);
		if (!g) return gs.length === 1 && gs[0].blocked ? gs[0].blocked : "Elige el Regalo";
		if (g.blocked) return g.blocked;
	}
	const asesoras = advisorsPending(app.advisors);
	if (asesoras) return asesoras;
	if (app.option.kind === KIND_DOS_X_UNO) {
		if (!app.giftEnrollmentId) return "Elige el Curso Regalado";
		if (app.giftIssues === null) return "Revisando el Curso Regalado…";
		if (app.giftIssues.length) return app.giftIssues.join(" ");
	}
	return null;
};
